import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Share2, Loader2, Send, MessageSquare, Mail, Copy, Check, Link2 } from "lucide-react";
import { toast } from "sonner";

export default function ShareFriend() {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("sms");
  const [contact, setContact] = useState("");
  const [sending, setSending] = useState(false);
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open && !info) api.get("/referrals/me").then((r) => setInfo(r.data)).catch(() => {});
  }, [open, info]);

  const copyLink = async () => {
    if (!info?.referral_link) return;
    try {
      await navigator.clipboard.writeText(info.referral_link);
      setCopied(true);
      toast.success("Invite link copied — share it anywhere!");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Couldn't copy the link"); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!contact.trim()) { toast.error(channel === "sms" ? "Enter a mobile number" : "Enter an email address"); return; }
    setSending(true);
    try {
      await api.post("/share/invite", { channel, contact: contact.trim() });
      toast.success(`Invite sent by ${channel === "sms" ? "text" : "email"} — thanks for spreading the word!`);
      setContact("");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't send the invite");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="share-friend-button"
          className="animate-flash-twice gap-2 rounded-full border-2 border-kilted-gold text-kilted-green hover:bg-kilted-gold/10">
          <Share2 className="h-4 w-4" /> Share with a friend
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="share-friend-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-kilted-green"><Share2 className="h-5 w-5 text-kilted-gold" /> Share The Kilted Courier</DialogTitle>
          <DialogDescription>Know someone who needs a reliable local courier? Send them an invite — you'll both get $5 credit once their first delivery is completed.</DialogDescription>
        </DialogHeader>
        <Tabs value={channel} onValueChange={(v) => { setChannel(v); setContact(""); }} className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="sms" data-testid="share-tab-sms"><MessageSquare className="mr-1.5 h-4 w-4" /> Text</TabsTrigger>
            <TabsTrigger value="email" data-testid="share-tab-email"><Mail className="mr-1.5 h-4 w-4" /> Email</TabsTrigger>
          </TabsList>
          <form onSubmit={send}>
            <TabsContent value="sms" className="mt-4 space-y-2">
              <Label htmlFor="share-phone">Friend's mobile</Label>
              <Input id="share-phone" type="tel" inputMode="tel" placeholder="0412 345 678" value={contact} onChange={(e) => setContact(e.target.value)} data-testid="share-phone-input" />
            </TabsContent>
            <TabsContent value="email" className="mt-4 space-y-2">
              <Label htmlFor="share-email">Friend's email</Label>
              <Input id="share-email" type="email" placeholder="friend@example.com" value={contact} onChange={(e) => setContact(e.target.value)} data-testid="share-email-input" />
            </TabsContent>
            <Button type="submit" disabled={sending} data-testid="share-send-button" className="mt-4 w-full gap-2 bg-kilted-green hover:bg-kilted-greenLight">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send invite
            </Button>
          </form>
        </Tabs>
        <div className="mt-4 border-t border-border pt-4">
          <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Link2 className="h-3.5 w-3.5" /> Or copy your invite link</Label>
          <div className="mt-2 flex items-center gap-2">
            <Input readOnly value={info?.referral_link || "Loading your invite link…"} data-testid="share-link-input" className="min-w-0 flex-1 text-xs" onFocus={(e) => e.target.select()} />
            <Button type="button" variant="outline" onClick={copyLink} disabled={!info?.referral_link} data-testid="share-copy-link-button"
              className="shrink-0 gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
